import type { MappedNotification } from 'common/header/notifications/Notifications.types';
import { useCallback } from 'react';
import { useDateTimeFormatter } from '@beeline.com/bp-design-system';
import { getExportExtendedTitle } from 'common/header/notifications/hooks/title/export';
import { getWorkdayOrgSyncTreeTitle } from 'common/header/notifications/hooks/title/workdayOrgSyncTree';
import { getGlobalIdTitle } from 'common/header/notifications/hooks/title/globalID';
import { getTaskAssignedTitle, getTaskResolvedTitle } from 'common/header/notifications/hooks/title/task';
import { getImportTitle } from 'common/header/notifications/hooks/title/import';
import { getSupplierRelationShipChangeTitle } from 'common/header/notifications/hooks/title/supplierRelationship';
import { getAuditReportTitle } from 'common/header/notifications/hooks/title/auditReport';
import { getWorkflowTitle } from 'common/header/notifications/hooks/title/workflow';
import { useTenantLocale } from 'utils/hooks/useTenantConfig';
import { useFormatNotificationMessage, useNotificationsIntl } from '../../translations/useNotificationsIntl';

export const useNotificationTitle = () => {
  const format = useFormatNotificationMessage();
  const intl = useNotificationsIntl();
  const { dateTimeFormatter } = useDateTimeFormatter();
  const userLocale = useTenantLocale();

  const getNotificationTitle = useCallback(
    (
      notificationViewModel: Partial<MappedNotification>
    ): Pick<MappedNotification, 'title' | 'description' | 'iconType'> => {
      const common = {
        title: notificationViewModel.title || '',
        description: notificationViewModel.description || '',
        iconType: notificationViewModel.iconType ?? 'INFO'
      };
      const getExportNotificationText = getExportExtendedTitle;

      switch (notificationViewModel.type) {
        case 'CSV_EXPORT':
        case 'EXCEL_EXPORT': {
          return {
            ...common,
            ...getExportNotificationText(format, notificationViewModel as MappedNotification, userLocale)
          };
        }
        case 'WORKDAY_ORG_TREE_SYNC':
          return {
            ...common,
            ...getWorkdayOrgSyncTreeTitle(notificationViewModel, intl)
          };
        case 'GLOBAL_ID_RUN':
          return getGlobalIdTitle(notificationViewModel, intl);
        case 'TASK_ASSIGNED':
          return {
            ...common,
            ...getTaskAssignedTitle(intl)
          };
        case 'TASK_RESOLVED':
          return {
            ...common,
            ...getTaskResolvedTitle(intl)
          };
        case 'IMPORT_UCI': {
          return {
            ...common,
            ...getImportTitle(notificationViewModel, format, dateTimeFormatter)
          };
        }
        case 'IMPORT':
        case 'USER_IMPORT':
        case 'PERSON_EVENT_IMPORT': {
          return {
            ...common,
            title: getImportTitle(notificationViewModel, format, dateTimeFormatter).title
          };
        }
        case 'SUPPLIER_RELATIONSHIP_CHANGE': {
          return {
            ...common,
            ...getSupplierRelationShipChangeTitle(notificationViewModel, intl)
          };
        }
        case 'AUDIT_REPORT': {
          return {
            ...common,
            ...getAuditReportTitle(intl)
          };
        }
        case 'PING': {
          return {
            ...common,
            title: intl.pingTitle
          };
        }
        case 'WORKFLOW_NOTIFICATION': {
          return {
            ...common,
            ...getWorkflowTitle(notificationViewModel)
          };
        }
        default:
          return common;
      }
    },
    [dateTimeFormatter, format, intl, userLocale]
  );

  return getNotificationTitle;
};
